export type Vec2 = readonly [number, number];


/** Lazy expression that evaluates to Vec2 using resolved context */
export type Vec2Expr = Vec2 | ((ctx: ResolvedCtx) => Vec2);

/** Lazy expression that evaluates to Rect using resolved context */
export type RectExpr = Rect | ((ctx: ResolvedCtx) => Rect);

/** Lazy expression that evaluates to number using resolved context */
export type NumExpr = number | ((ctx: ResolvedCtx) => number);

/** Helper to check if value is an expression (function) */
export function isExpr<T>(v: T | ((ctx: ResolvedCtx) => T)): v is (ctx: ResolvedCtx) => T {
    return typeof v === 'function';
}

export type Anchor =
    | "origin"      // (0, 0) - the transform origin point
    | "topLeft"
    | "top"
    | "topRight"
    | "left"
    | "center"
    | "right"
    | "bottomLeft"
    | "bottom"
    | "bottomRight"
    | "baselineLeft"
    | "baseline";

export interface SceneObject {
    readonly id: symbol;
}

export interface GroupObject extends SceneObject {
    readonly members: SceneObject[];
    readonly isGroup: true;
}

export type ObjRef = SceneObject | GroupObject;

export function isGroup(obj: SceneObject): obj is GroupObject {
    return 'isGroup' in obj && (obj as GroupObject).isGroup === true;
}

export interface Transform {
    translate: Vec2;
    rotate: number;   // radians
    scale: number;
    scaleX?: number;  // optional non-uniform scale
    scaleY?: number;
}

export interface Rect {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface TextOpts {
    at?: Vec2;
    pin?: Anchor;
    font?: string;
    size?: number;
    color?: string;
}

export interface ImageOpts {
    at?: Vec2;
    pin?: Anchor;
    width?: number;
    height?: number;
}

export interface RectOpts {
    at?: Vec2;
    pin?: Anchor;
    width: number;
    height: number;
    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    cornerRadius?: number;
}

export interface ResolvedCtx {
    getTransform(obj: SceneObject): Transform;
    getOpacity(obj: SceneObject): number;
    /** Get a named numeric property set via ctx.prop() / selection.prop() */
    getProp(obj: SceneObject, name: string): number | undefined;
}

export type ImageSource =
    | { kind: "url"; src: string }
    | { kind: "image"; image: HTMLImageElement }
    | { kind: "bitmap"; bitmap: ImageBitmap }
    | { kind: "canvas"; canvas: HTMLCanvasElement };

/**
 * Minimal interface needed to drive the animation system.
 * Implement this to plug in an external document model (e.g. a vector editor's
 * node tree) without needing a canvas renderer.
 *
 * Use with:
 *   ctx.reset(myGeometry)
 *   timeline.evaluate(t, ctx)
 *   const solved = ctx.solve(myGeometry)
 *   // apply solved.getTransform(obj) / solved.getOpacity(obj) to your own renderer
 */
export interface SceneGeometry {
    /** All objects that exist in the document */
    objects(): Iterable<SceneObject>;
    /** Axis-aligned bounding box of obj in its own local coordinate space */
    getBounds(obj: SceneObject): Rect;
    /**
     * Position of the named anchor within the object's local coordinate space.
     * For standard axis-aligned objects, use the exported `resolveAnchorFromBounds`
     * helper — it computes this from getBounds automatically.
     */
    resolveAnchor(obj: SceneObject, anchor: Anchor): Vec2;
    /** The transform the object had before any animation (typically its authored position) */
    getInitialTransform(obj: SceneObject): Transform;
    /** The opacity the object had before any animation. Defaults to 1 when omitted. */
    getInitialOpacity?(obj: SceneObject): number;
    /** The initial value of a named numeric property before any animation. Returns undefined when not applicable. */
    getInitialProp?(obj: SceneObject, name: string): number | undefined;
    /**
     * Y position of the text baseline in local space, or null for non-text objects.
     * Optional — only used by the debug overlay. Return null when not applicable.
     */
    getBaseline?(obj: SceneObject): number | null;
}

/** Full built-in scene: object creation + geometry queries + canvas rendering. */
export interface Scene extends SceneGeometry {
    text(content: string, opts?: TextOpts): SceneObject;
    rect(opts: RectOpts): SceneObject;
    bg(color: string): SceneObject;
    image(src: string | HTMLImageElement | ImageBitmap | HTMLCanvasElement | ImageSource, opts: ImageOpts): SceneObject;
    getBaseline(obj: SceneObject): number | null;
    render(ctx: ResolvedCtx, canvas: HTMLCanvasElement, dpr?: number): void;
}

export type EaseFn = (u: number) => number;
export type BodyFn<TCtx> = (ctx: TCtx, u: number) => void;
export type InstantBodyFn<TCtx> = (ctx: TCtx) => void;

export interface Segment<TCtx> {
    id: number;
    start: number;
    end: number;
    ease: EaseFn;
    body: BodyFn<TCtx>;
}

export interface TimelineAPI<TCtx> {
    seq(fn: () => void): void;
    mark(name: string): void;
    tween(dur: number, ease: EaseFn | null | undefined, body: BodyFn<TCtx>): void;
    instant(body: InstantBodyFn<TCtx>): void;
    /** Wait/pause for a duration (sugar for tween with no-op body) */
    wait(dur: number): void;
    with(name: string, offsetMs: number, fn: () => void): void;
    overlap(offsetMs: number, fn: () => void): void;
    par(fn: () => void): void;
}

export interface Timeline<TCtx> {
    duration: number;
    segs: Segment<TCtx>[];
    evaluate(tMs: number, ctx: TCtx): void;
}

export interface TransformMethods {
    to(v: NumExpr, u: number): void;
    by(delta: NumExpr, u: number): void;
}

/** Target for resize - can be a number (width-based uniform) or an object */
export type ResizeTarget =
    | number                              // width-based uniform (default)
    | { width: number }                   // width-based uniform (explicit)
    | { height: number }                  // height-based uniform
    | { width: number; height: number }   // non-uniform
    | { fit: number };                    // fit within a box

/** Resize target that supports expressions */
export type ResizeTargetExpr =
    | NumExpr                                     // width-based uniform
    | { width: NumExpr }                          // width-based uniform (explicit)
    | { height: NumExpr }                         // height-based uniform
    | { width: NumExpr; height: NumExpr }         // non-uniform
    | { fit: NumExpr };                           // fit within a box

/** Methods for pixel-based resizing */
export interface ResizeMethods {
    to(target: ResizeTargetExpr, u: number): void;
    by(delta: ResizeTargetExpr, u: number): void;
}

/** Options for toBBox */
export interface FitToBBoxOpts {
    pad?: Padding;
}

/** Methods for fitting an object to a bounding box (resize + center pin) */
export interface FitMethods {
    /** Fit to an existing BBoxHandle */
    to(bbox: BBoxHandle, u: number): void;
    /** Measure a group/object and fit to it */
    toBBox(target: SceneObject | GroupObject, u: number, opts?: FitToBBoxOpts): void;
}

/** Padding specification - uniform number or per-side values */
export type Padding = number | {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
};

/** Lazy function that evaluates Vec2 from resolved context (always callable) */
export type Vec2Fn = (ctx: ResolvedCtx) => Vec2;

/** Lazy function that evaluates number from resolved context (always callable) */
export type NumFn = (ctx: ResolvedCtx) => number;

/** Lazy function that evaluates Rect from resolved context (always callable) */
export type RectFn = (ctx: ResolvedCtx) => Rect;

/** Handle for measured bounding box - returns lazy expressions that are always callable */
export interface BBoxHandle {
    /** Add uniform or per-side padding */
    pad(p: Padding): BBoxHandle;
    /** Get size as [width, height] - always returns a callable function */
    size(): Vec2Fn;
    /** Get width in pixels - always returns a callable function */
    width(): NumFn;
    /** Get height in pixels - always returns a callable function */
    height(): NumFn;
    /** Get { width, height } for use with resize().to() */
    whSize(): { width: NumFn; height: NumFn };
    /** Get center point - always returns a callable function */
    center(): Vec2Fn;
    /** Get position of any anchor on the bbox - always returns a callable function */
    anchor(a: Anchor): Vec2Fn;
    /** Get the raw rect - always returns a callable function */
    toRect(): RectFn;
}

export interface PinMethods {
    to(pos: Vec2Expr, u: number): void;
    by(delta: Vec2Expr, u: number): void;
}

export interface GroupBuilder extends GroupObject {
    pin(anchor: Anchor): PinMethods;
    scale(): TransformMethods;
    rotate(): TransformMethods;
    opacity(): TransformMethods;
}

export interface LayoutOpts {
    axis?: "x" | "y";
    align?: Anchor;
    gap?: number;
}

export interface AlignOpts {
    anchor?: Anchor;
}

export interface LayoutBuilder {
    to(u: number): GroupBuilder;
}

/** Fluent selection of one or more objects — the new primary animation API.
 *
 * Usage: ctx.select(a, b, c).layout({ axis: "x", gap: 30 }, u).pin("center", Screen.center(), u)
 */
export interface Selection {
    readonly objects: ObjRef[];

    // ── Layout (arrange members, return self for chaining) ──────────────────
    layout(opts: LayoutOpts, u: number): Selection;
    inline(u: number): Selection;
    stack(u: number): Selection;
    paragraph(u: number): Selection;
    align(anchor: Anchor, u: number): Selection;

    // ── Positioning ─────────────────────────────────────────────────────────
    pin(anchor: Anchor, pos: Vec2Expr, u: number): Selection;
    pinBy(anchor: Anchor, delta: Vec2Expr, u: number): Selection;

    // ── Transforms (group-aware) ─────────────────────────────────────────────
    rotate(rad: NumExpr, u: number): Selection;
    rotateBy(deltaRad: NumExpr, u: number): Selection;
    scale(v: NumExpr, u: number): Selection;
    opacity(v: NumExpr, u: number): Selection;

    // ── Per-object transforms ────────────────────────────────────────────────
    scaleX(v: NumExpr, u: number): Selection;
    scaleY(v: NumExpr, u: number): Selection;
    resize(target: ResizeTargetExpr, u: number): Selection;
    width(v: NumExpr, u: number): Selection;
    height(v: NumExpr, u: number): Selection;

    // ── Fitting ──────────────────────────────────────────────────────────────
    fit(bbox: BBoxHandle, u: number): Selection;
    fitToBBox(target: Selection | ObjRef, u: number, opts?: FitToBBoxOpts): Selection;

    // ── Measurement ──────────────────────────────────────────────────────────
    bbox(pad?: Padding): BBoxHandle;

    // ── Generic property channels ─────────────────────────────────────────────
    /** Animate any named numeric property (e.g. 'cornerRadius', 'strokeWidth') */
    prop(name: string, value: NumExpr, u: number): Selection;
}

export interface AnimCtx {
    /** Select one or more objects for fluent animation chaining */
    select(...objects: ObjRef[]): Selection;

    // ── Generic property channels ────────────────────────────────────────────
    /** Animate any named numeric property (corner-radius, stroke-width, …) */
    prop(obj: SceneObject, name: string): TransformMethods;

    // ── Legacy object-first API (still supported) ────────────────────────────
    pin(obj: SceneObject | GroupObject, anchor: Anchor): PinMethods;
    scale(obj: SceneObject | GroupObject): TransformMethods;
    scaleX(obj: SceneObject): TransformMethods;
    scaleY(obj: SceneObject): TransformMethods;
    rotate(obj: SceneObject | GroupObject): TransformMethods;
    opacity(obj: SceneObject | GroupObject): TransformMethods;
    /** Set width in pixels (converts to scaleX internally) */
    width(obj: SceneObject): TransformMethods;
    /** Set height in pixels (converts to scaleY internally) */
    height(obj: SceneObject): TransformMethods;
    /** Resize in pixels - uniform or non-uniform */
    resize(obj: SceneObject): ResizeMethods;
    /** Fit object to a bounding box (resize + center pin) */
    fit(obj: SceneObject): FitMethods;
    /** Measure the bounding box of an object or group */
    bbox(obj: SceneObject | GroupObject): BBoxHandle;
    group(...objects: SceneObject[]): GroupBuilder;
    layout(...args: (ObjRef | LayoutOpts)[]): LayoutBuilder;
    inline(...args: (ObjRef | number)[]): LayoutBuilder;
    stack(...args: (ObjRef | number)[]): LayoutBuilder;
    paragraph(...args: (ObjRef | number)[]): LayoutBuilder;
    align(...args: (ObjRef | AlignOpts)[]): LayoutBuilder;
    center(...objects: ObjRef[]): LayoutBuilder;

    // ── Multi-pass timeline evaluation (optional) ────────────────────────────
    // Lets the timeline freeze the position constraints of motions that have
    // already ended at their end-of-motion state, so they stop tracking targets
    // that keep moving during later (possibly overlapping) motions.
    /** Begin a fresh pass, clearing per-pass state but keeping frozen pins. */
    beginTimelinePass?(): void;
    /** Announce which segment body is about to run. */
    beginSegmentScope?(segmentId: number): void;
    /** End a pass, freezing the pins of the segments that end at this boundary. */
    endTimelinePass?(freezeSegmentIds: number[]): void;
}

export interface PinConstraint {
    anchor: Anchor;
    world: Vec2;
    weight?: number;
    priority?: number;
    source?: string;
}

export interface ChannelState {
    scale?: number;
    scaleX?: number;
    scaleY?: number;
    rotate?: number;
    opacity?: number;
    /** Arbitrary named numeric properties (corner-radius, stroke-width, etc.) */
    props?: Record<string, number>;
}
